package com.writerslab

/**
 * Mirror of the logistic-regression weights that drive `quality` server-side.
 * Source of truth: `book-analyzer-py/constants.py` (means/stds/coefs) and
 * `book-analyzer-py/metrics/style.py` (feature ordering, RU model).
 *
 * Only the twelve user-visible 1-10 score features are listed here; the four
 * raw-rate features (weak_verbs / intensifiers / bureaucratese / sentence
 * variety) don't surface in the UI grid, so we can't visually flag them.
 *
 * Keep these numbers in sync if the analyzer is retrained — there's no
 * runtime check.
 */
private data class FeatureSpec(val mean: Double, val std: Double, val coef: Double)

private val RU_QUALITY_FEATURES: Map<Metric, FeatureSpec> = mapOf(
    Metric.COMPLEXITY     to FeatureSpec(4.876, 2.891, +0.106),
    Metric.VOCABULARY     to FeatureSpec(4.935, 2.894, +0.106),
    Metric.PACE           to FeatureSpec(6.363, 2.899, -0.042),
    Metric.DIALOGUE       to FeatureSpec(6.095, 1.930, +0.448),
    Metric.EXPRESSIVENESS to FeatureSpec(5.410, 2.523, +0.112),
    Metric.COHERENCE      to FeatureSpec(5.046, 2.082, -0.154),
    Metric.ARC_VOLATILITY to FeatureSpec(6.231, 2.475, -0.176),
    Metric.VIVIDNESS      to FeatureSpec(4.923, 1.840, +0.156),
    Metric.PATHOS         to FeatureSpec(5.067, 2.835, +0.275),
    Metric.MORALITY       to FeatureSpec(5.004, 2.456, -0.173),
    Metric.AUTHOR_STANCE  to FeatureSpec(4.625, 2.536, +0.069),
    Metric.VOICES         to FeatureSpec(4.627, 3.100, +0.279),
)

// EN model — only the first 12 features (those that share the 1-10 scale
// with the API output). The remaining features in `metrics/style.py`'s
// `EN_QUALITY` (weak_verbs..mean_dependency_length) live in raw units
// and the API value is post-normalised, so we can't compute z-scores
// for them on the front-end without a full second normalisation table.
// Coefs/means/stds copied verbatim from `metrics/style.py`.
private val EN_QUALITY_FEATURES: Map<Metric, FeatureSpec> = mapOf(
    Metric.COMPLEXITY     to FeatureSpec(4.8428, 2.8964, -0.7835),
    Metric.VOCABULARY     to FeatureSpec(5.7119, 2.4844, +0.4546),
    Metric.PACE           to FeatureSpec(5.8126, 2.8737, -0.4481),
    Metric.DIALOGUE       to FeatureSpec(4.0717, 2.2304, +0.1676),
    Metric.EXPRESSIVENESS to FeatureSpec(5.5925, 2.9495, -0.6297),
    Metric.COHERENCE      to FeatureSpec(5.3874, 2.8201, -0.2210),
    Metric.ARC_VOLATILITY to FeatureSpec(7.3270, 1.8419, -0.2566),
    Metric.VIVIDNESS      to FeatureSpec(5.1799, 2.8955, -0.1087),
    Metric.PATHOS         to FeatureSpec(4.8742, 2.7916, -0.1357),
    Metric.MORALITY       to FeatureSpec(5.5220, 2.8245, -0.1692),
    Metric.AUTHOR_STANCE  to FeatureSpec(5.4553, 2.7097, +0.2285),
    Metric.VOICES         to FeatureSpec(1.8126, 3.0201, +0.2692),
)

private fun featuresFor(language: Language?): Map<Metric, FeatureSpec>? = when (language) {
    Language.RU -> RU_QUALITY_FEATURES
    Language.ENG -> EN_QUALITY_FEATURES
    else -> null
}

/** Direction of a metric's drag — useful for telling the user *what* to
 *  change. `TOO_LOW` ⇒ raise this metric; `TOO_HIGH` ⇒ lower it. */
enum class DragDirection { TOO_LOW, TOO_HIGH }

/**
 * Returns the metrics whose negative contribution to the overall quality
 * score exceeds [threshold] (in standardised-coefficient units), keyed
 * to the direction that was wrong. "Negative contribution" means the
 * metric pulled the predicted quality DOWN; we map that into TOO_LOW or
 * TOO_HIGH by comparing the user's 1-10 value to the population mean
 * (also on a 1-10 scale) — that way we don't have to know the formula's
 * coefficient sign client-side.
 *
 * Backend-emitted `qualityContributions` are authoritative when present
 * (they cover EN-extras whose raw units we can't z-score from the
 * post-normalised 1-10 API value). When the backend hasn't shipped them,
 * we fall back to the local RU/ENG coefficient tables so the feature
 * keeps working against older deployments.
 *
 * Returns up to [maxCount] biggest drags. Empty when:
 *   - quality wasn't computed (`result.quality == null`);
 *   - quality is already in the "great" band (≥ 8) — nothing to flag;
 *   - neither the backend nor a local table covers the language.
 */
fun findQualityDrags(
    result: AnalysisResult,
    // Backend contributions are computed in raw units; for raw-unit
    // formula features (EN-extras, RU's last 4 style features) the API
    // value is post-normalised, so a "mild drag" can show up while the
    // user's bar fill sits right next to the reference tick. We require
    // the contribution to be *clearly* negative AND the user's
    // post-normalised value to differ from the reference by a visible
    // amount before painting the card red.
    contributionThreshold: Double = 0.20,
    referenceGap: Double = 1.0,
    maxCount: Int = 8,
): Map<Metric, DragDirection> {
    val q = result.quality ?: return emptyMap()
    if (q >= 8) return emptyMap()

    val candidates = result.profile + result.character + result.style
    val byId = candidates.associateBy { it.id }

    // Authoritative path — backend has computed the actual signed
    // contribution per metric using raw values + correct units.
    val backend = result.qualityContributions
    val contributions: Map<Metric, Double> = backend.ifEmpty {
        // Local fallback (older backend without the field).
        val features = featuresFor(result.language) ?: return emptyMap()
        candidates.mapNotNull { item ->
            val spec = features[item.id] ?: return@mapNotNull null
            item.id to (item.value - spec.mean) / spec.std * spec.coef
        }.toMap()
    }

    val ranked = mutableListOf<Triple<Metric, Double, DragDirection>>()
    for ((metric, contribution) in contributions) {
        val drag = -contribution
        if (drag <= contributionThreshold) continue
        val item = byId[metric] ?: continue
        val ref = item.reference ?: continue
        // Skip descriptive metrics (Pace, Dialogue, Complexity, mood
        // swings, etc): their LR coef reflects mass-market correlation,
        // not a craft principle, so flagging them as drag misleads the
        // user. The bar fill on these cards is also forced to violet —
        // both signals must agree.
        if (metricGoodDirection(result, metric) == GoodDirection.NONE) continue
        // Visual sanity check: don't flag a card whose bar fill is
        // already sitting on the reference tick — even when the formula
        // technically says "mild drag", the user reads "matches average".
        if (kotlin.math.abs(item.value - ref) < referenceGap) continue
        val direction = if (item.value < ref) DragDirection.TOO_LOW else DragDirection.TOO_HIGH
        ranked += Triple(metric, drag, direction)
    }
    return ranked
        .sortedByDescending { it.second }
        .take(maxCount)
        .associate { it.first to it.third }
}

/**
 * Mirror of [findQualityDrags] for *positive* contribution: returns the
 * metrics that pull overall quality UP the most.
 *
 * Same gating rules as drags: explicit contribution must clear the
 * threshold, the user's value must differ from the reference by a visible
 * amount, and descriptive metrics (no good-direction) are excluded so the
 * highlight matches the green bar fill on the expanded card.
 *
 * Returned set is sorted insertion-wise from biggest boost to smallest —
 * callers that need ordering should iterate the set as-is.
 */
fun findQualityBoosts(
    result: AnalysisResult,
    contributionThreshold: Double = 0.20,
    referenceGap: Double = 1.0,
    maxCount: Int = 6,
): Set<Metric> {
    if (result.quality == null) return emptySet()

    val candidates = result.profile + result.character + result.style
    val byId = candidates.associateBy { it.id }

    val backend = result.qualityContributions
    val contributions: Map<Metric, Double> = backend.ifEmpty {
        val features = featuresFor(result.language) ?: return emptySet()
        candidates.mapNotNull { item ->
            val spec = features[item.id] ?: return@mapNotNull null
            item.id to (item.value - spec.mean) / spec.std * spec.coef
        }.toMap()
    }

    val ranked = mutableListOf<Pair<Metric, Double>>()
    for ((metric, contribution) in contributions) {
        if (contribution <= contributionThreshold) continue
        val item = byId[metric] ?: continue
        val ref = item.reference ?: continue
        if (metricGoodDirection(result, metric) == GoodDirection.NONE) continue
        if (kotlin.math.abs(item.value - ref) < referenceGap) continue
        ranked += metric to contribution
    }
    return ranked
        .sortedByDescending { it.second }
        .take(maxCount)
        .map { it.first }
        .toSet()
}
